/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';
import { LOGGED_IN_USER_DETAILS_API } from '../../constants/UserAndRoles';
import { ERR_CODE_403 } from '../../utils/common/utils';
import { getEnv } from 'src/utils/env.vite';
import { IRole } from 'src/types/UserAndRoles.type';

interface ITowerPcUserDetails {
  userName: string;
  uid: string;
  mail: string;
}

interface ISetNameAccessToken {
  givenName: string;
  sn: string;
  uid: string;
  accessToken: string;
  mail: string;
}

export interface IGenesisUserRolePermissionState {
  isFetching: boolean;
  permissions: string[];
  isAuthorized: boolean | null;
  error: null | string;
  userId: number | null;
  userName: string;
  role: IRole[];
  isEosUser: boolean;
  accessToken: string;
  uid: string;
  mail: string;
}

const initialState: IGenesisUserRolePermissionState = {
  isFetching: false,
  permissions: [],
  isAuthorized: null,
  error: null,
  userId: null,
  userName: '',
  role: [],
  accessToken: '',
  uid: '',
  mail: '',
  isEosUser: false,
};

export const fetchGenesisRolePermission = createAsyncThunk(
  'genesisUserRolePermission/fetchGenesisUserRolePermission',
  async ({
    accessToken,
    userName,
  }: {
    accessToken: string;
    userName?: string;
  }) =>
    axios
      .get(
        `${
          getEnv('VITE_APP_GEN_API_BASE_URL') ?? ''
        }${LOGGED_IN_USER_DETAILS_API}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            TowerPcUserName: userName,
          },
        }
      )
      .then((response: AxiosResponse) => {
        return {
          permissions:
            response?.data?.policies?.map(
              (item: { policyName: string }) => item.policyName
            ) || [],
          userId: response?.data?.user?.userId,
          role: response?.data?.user?.role,

          isEosUser: response?.data?.user?.isEosUser ?? false,
        };
      })
      .catch((error) => {
        if (error.request.status === 0 && error.response === undefined) {
          error.message = 'CORS';
        }
        if (error.request.status === ERR_CODE_403) {
          error.message = String(ERR_CODE_403);
        }
        throw error;
      })
);

const genesisRolePermission = createSlice({
  name: 'genesisUserRolePermission',
  initialState,
  reducers: {
    setNameAccessToken: (
      state: IGenesisUserRolePermissionState,
      { payload }: PayloadAction<ISetNameAccessToken>
    ) => {
      state.userName = `${payload.givenName} ${payload.sn}`;
      state.accessToken = payload.accessToken;
      state.uid = payload.uid;
      state.mail = payload.mail;
    },
    setTowerPcUserDetails: (
      state: IGenesisUserRolePermissionState,
      { payload }: PayloadAction<ITowerPcUserDetails>
    ) => {
      state.userName = payload.userName;
      state.uid = payload.uid;
      state.mail = payload.mail;
    },
    logOut: (state: IGenesisUserRolePermissionState) => {
      state.isAuthorized = null;
      state.userName = '';
      state.accessToken = '';
      state.uid = '';
      state.permissions = [];
      state.mail = '';
      state.role = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchGenesisRolePermission.pending,
        (state: IGenesisUserRolePermissionState) => {
          state.isFetching = true;
          state.error = null;
        }
      )
      .addCase(
        fetchGenesisRolePermission.fulfilled,
        (
          state: IGenesisUserRolePermissionState,
          {
            payload,
          }: PayloadAction<{
            permissions: string[];
            userId: number;
            role: IRole[];
            isEosUser: boolean;
          }>
        ) => {
          state.permissions = payload.permissions;
          state.userId = payload.userId;
          state.role = payload.role;
          state.isEosUser = payload.isEosUser;
          state.isFetching = false;
          state.isAuthorized = true;
          state.error = null;
        }
      )
      .addCase(
        fetchGenesisRolePermission.rejected,
        (state: IGenesisUserRolePermissionState, payload: any) => {
          state.permissions = [];
          state.userId = null;
          state.isFetching = false;
          state.isAuthorized = false;
          state.error = payload.error.message;
        }
      );
  },
});

export const userRoleActions = genesisRolePermission.actions;
export default genesisRolePermission.reducer;
